import { useState, useEffect } from 'react'
import { fmtDate } from '../../utils/format'
import {
  PlusIcon,
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  CheckCircleIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  BuildingOfficeIcon,
  CubeIcon
} from '@heroicons/react/24/outline'

interface Zone {
  id: number
  name: string
  code: string
  description: string
  type: 'production' | 'storage' | 'preparation' | 'packaging' | 'other'
  type_name: string
  area_m2?: number
  status: 'active' | 'inactive' | 'maintenance'
  status_name: string
  risk_level?: string[]
  notes?: string
  pending_cleaning_tasks_count: number
  overdue_cleaning_tasks_count: number
  has_overdue_tasks: boolean
  created_at: string
  updated_at: string
}

interface ZoneElement {
  id: number
  element_type: string
  element_type_name: string
  name: string
  cleaning_frequency_hours: number
  readable_frequency: string
  priority: string
  priority_name: string
  status: string
  is_cleaning_overdue: boolean
  next_cleaning_due?: string
  pending_tasks_count: number
}

interface ElementType {
  id: number
  code: string
  name: string
  description?: string
  priority: string
  is_active: boolean
}

interface CleaningFrequency {
  id: number
  code: string
  name: string
  description?: string
  hours: number
  is_active: boolean
  readable_frequency: string
}

interface ZoneDetails extends Zone {
  elements: ZoneElement[]
  refrigerators: Array<{
    id: number
    name: string
    code: string
    type_name: string
    status_name: string
  }>
}

const API_BASE_URL = '/api/v1/dev'

export default function Zones() {
  const [zones, setZones] = useState<Zone[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedZone, setSelectedZone] = useState<ZoneDetails | null>(null)
  const [editingZone, setEditingZone] = useState<Zone | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [elementTypes, setElementTypes] = useState<ElementType[]>([])
  const [cleaningFrequencies, setCleaningFrequencies] = useState<CleaningFrequency[]>([])
  const [editingFrequencies, setEditingFrequencies] = useState(false)
  const [frequencyUpdates, setFrequencyUpdates] = useState<{[key: number]: number}>({})

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    type: 'production',
    area_m2: '',
    status: 'active',
    notes: ''
  })
  const [elementFrequencies, setElementFrequencies] = useState<{[key: number]: number}>({})

  // Fetch zones
  const fetchZones = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetch(`${API_BASE_URL}/zones`)
      const data = await response.json()

      if (response.ok) {
        setZones(data.data || [])
      } else {
        setError('Error al cargar zonas')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error fetching zones:', err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch element types and cleaning frequencies
  const fetchReferenceData = async () => {
    try {
      const [elementTypesRes, frequenciesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/zones/element-types`),
        fetch(`${API_BASE_URL}/zones/cleaning-frequencies`)
      ])

      if (elementTypesRes.ok) {
        const elementTypesData = await elementTypesRes.json()
        setElementTypes(elementTypesData.data || [])
      }

      if (frequenciesRes.ok) {
        const frequenciesData = await frequenciesRes.json()
        setCleaningFrequencies(frequenciesData.data || [])
      }
    } catch (err) {
      console.error('Error fetching reference data:', err)
    }
  }

  useEffect(() => {
    fetchZones()
    fetchReferenceData()
  }, [])

  // Debug log
  useEffect(() => {
    console.log('ElementTypes:', elementTypes)
    console.log('CleaningFrequencies:', cleaningFrequencies)
  }, [elementTypes, cleaningFrequencies])

  // Filter zones
  const filteredZones = zones.filter(zone => {
    const typeMatch = typeFilter === 'all' || zone.type === typeFilter
    const statusMatch = statusFilter === 'all' || zone.status === statusFilter
    return typeMatch && statusMatch
  })

  // Get type badge
  const getTypeBadge = (type: string) => {
    const typeConfig = {
      production: { color: 'bg-primary-50 text-primary-700 border border-primary-200', text: 'Producción', icon: CubeIcon },
      storage: { color: 'bg-blue-50 text-blue-700 border border-blue-200', text: 'Almacén', icon: BuildingOfficeIcon },
      preparation: { color: 'bg-green-50 text-green-700 border border-green-200', text: 'Preparación', icon: MapPinIcon },
      packaging: { color: 'bg-purple-50 text-purple-700 border border-purple-200', text: 'Empaquetado', icon: CubeIcon },
      other: { color: 'bg-gray-50 text-gray-700 border border-gray-200', text: 'Otro', icon: MapPinIcon }
    }

    const config = typeConfig[type as keyof typeof typeConfig] || typeConfig.production
    const Icon = config.icon

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </span>
    )
  }

  // Get status badge
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-50 text-green-700 border border-green-200', icon: CheckCircleIcon, text: 'Activa' },
      maintenance: { color: 'bg-accent-50 text-accent-700 border border-accent-200', icon: WrenchScrewdriverIcon, text: 'Mantenimiento' },
      inactive: { color: 'bg-gray-50 text-gray-600 border border-gray-200', icon: ClockIcon, text: 'Inactiva' }
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active
    const Icon = config.icon

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </span>
    )
  }

  // Get priority badge
  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      critical: { color: 'bg-red-50 text-red-700 border border-red-200', text: 'Crítico' },
      high: { color: 'bg-orange-50 text-orange-700 border border-orange-200', text: 'Alto' },
      medium: { color: 'bg-yellow-50 text-yellow-700 border border-yellow-200', text: 'Medio' },
      low: { color: 'bg-green-50 text-green-700 border border-green-200', text: 'Bajo' }
    }

    const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.medium

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    )
  }

  // Handle create or update
  const handleSave = async () => {
    setSaving(true)
    setError(null)

    try {
      const url = editingZone
        ? `${API_BASE_URL}/zones/${editingZone.id}`
        : `${API_BASE_URL}/zones`

      const method = editingZone ? 'PUT' : 'POST'

      const payload = {
        ...formData,
        area_m2: formData.area_m2 ? parseFloat(formData.area_m2) : null
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      const data = await response.json()

      if (response.ok) {
        const zoneId = editingZone ? editingZone.id : data.data.id

        // Update element frequencies if any were changed from default
        const hasFrequencyChanges = Object.keys(elementFrequencies).length > 0
        if (hasFrequencyChanges) {
          const elements = Object.entries(elementFrequencies).map(([elementTypeId, frequencyId]) => {
            // Find the element for this zone and element type
            const elementType = elementTypes.find(et => et.id === parseInt(elementTypeId))
            return {
              element_type: elementType?.code,
              cleaning_frequency_id: frequencyId
            }
          }).filter(el => el.element_type)

          if (elements.length > 0) {
            // We need to get the actual element IDs first
            const zoneResponse = await fetch(`${API_BASE_URL}/zones/${zoneId}`)
            const zoneData = await zoneResponse.json()
            if (zoneResponse.ok && zoneData.data.elements) {
              const elementUpdates = zoneData.data.elements.map((element: any) => {
                const elementType = elementTypes.find(et => et.code === element.element_type)
                const newFrequencyId = elementType ? elementFrequencies[elementType.id] : null
                return newFrequencyId ? {
                  element_id: element.id,
                  cleaning_frequency_id: newFrequencyId
                } : null
              }).filter(Boolean)

              if (elementUpdates.length > 0) {
                await fetch(`${API_BASE_URL}/zones/${zoneId}/elements/frequencies`, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ elements: elementUpdates }),
                })
              }
            }
          }
        }

        await fetchZones()
        setShowCreateModal(false)
        setEditingZone(null)
        resetForm()
      } else {
        if (data.errors) {
          const messages = Object.values(data.errors).flat().join('\n')
          setError(messages)
        } else {
          setError(data.message || 'Error al guardar la zona')
        }
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error saving zone:', err)
    } finally {
      setSaving(false)
    }
  }

  // Delete zone
  const deleteZone = async (id: number) => {
    if (!confirm('¿Está seguro de eliminar esta zona? Esto también eliminará todos sus elementos asociados.')) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/zones/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        await fetchZones()
      } else {
        const data = await response.json()
        setError(data.message || 'Error al eliminar la zona')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error deleting zone:', err)
    }
  }

  // View zone details
  const viewZone = async (zone: Zone) => {
    try {
      const response = await fetch(`${API_BASE_URL}/zones/${zone.id}`)
      const data = await response.json()

      if (response.ok) {
        setSelectedZone(data.data)
        setEditingFrequencies(false)
        setFrequencyUpdates({})
        setShowViewModal(true)
      } else {
        setError('Error al cargar detalles de la zona')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error fetching zone details:', err)
    }
  }

  // Open create modal
  const openCreateModal = () => {
    setEditingZone(null)
    resetForm()
    setShowCreateModal(true)
    setError(null)
  }

  // Edit zone
  const editZone = async (zone: Zone) => {
    setEditingZone(zone)
    setFormData({
      name: zone.name,
      code: zone.code,
      description: zone.description || '',
      type: zone.type,
      area_m2: zone.area_m2?.toString() || '',
      status: zone.status,
      notes: zone.notes || ''
    })

    // Load current element frequencies for editing
    try {
      const response = await fetch(`${API_BASE_URL}/zones/${zone.id}`)
      const data = await response.json()
      if (response.ok && data.data.elements) {
        const frequencies: {[key: number]: number} = {}
        data.data.elements.forEach((element: any) => {
          const elementType = elementTypes.find(et => et.code === element.element_type)
          const frequency = cleaningFrequencies.find(f => f.hours === element.cleaning_frequency_hours)
          if (elementType && frequency) {
            frequencies[elementType.id] = frequency.id
          }
        })
        setElementFrequencies(frequencies)
      }
    } catch (err) {
      console.error('Error loading zone elements:', err)
    }

    setShowCreateModal(true)
    setError(null)
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      type: 'production',
      area_m2: '',
      status: 'active',
      notes: ''
    })
    // Set default frequencies (daily) for all element types
    const defaultFrequencies: {[key: number]: number} = {}
    elementTypes.forEach(elementType => {
      const dailyFreq = cleaningFrequencies.find(f => f.code === 'daily')
      if (dailyFreq) {
        defaultFrequencies[elementType.id] = dailyFreq.id
      }
    })
    setElementFrequencies(defaultFrequencies)
  }

  // Update element frequencies
  const updateElementFrequencies = async () => {
    if (!selectedZone || Object.keys(frequencyUpdates).length === 0) return

    try {
      setSaving(true)
      setError(null)

      const elements = Object.entries(frequencyUpdates).map(([elementId, frequencyId]) => ({
        element_id: parseInt(elementId),
        cleaning_frequency_id: frequencyId
      }))

      const response = await fetch(`${API_BASE_URL}/zones/${selectedZone.id}/elements/frequencies`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ elements }),
      })

      if (response.ok) {
        // Refresh zone details
        await viewZone(selectedZone)
        setEditingFrequencies(false)
        setFrequencyUpdates({})
      } else {
        const data = await response.json()
        if (data.errors) {
          const messages = Object.values(data.errors).flat().join('\n')
          setError(messages)
        } else {
          setError(data.message || 'Error al actualizar frecuencias')
        }
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error updating frequencies:', err)
    } finally {
      setSaving(false)
    }
  }

  // Handle frequency change
  const handleFrequencyChange = (elementId: number, frequencyId: number) => {
    setFrequencyUpdates(prev => ({
      ...prev,
      [elementId]: frequencyId
    }))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Zonas</h1>
          <p className="mt-1 text-sm text-gray-500">
            Administra las zonas del establecimiento y sus elementos de limpieza
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Nueva Zona
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Cleaning Alerts */}
      {zones.filter(zone => zone.has_overdue_tasks).length > 0 && (
        <div className="bg-orange-50 border-l-4 border-orange-400 p-4">
          <div className="flex">
            <WrenchScrewdriverIcon className="h-5 w-5 text-orange-400" />
            <div className="ml-3">
              <p className="text-sm text-orange-700 font-medium">
                Alertas de Limpieza
              </p>
              <p className="mt-2 text-sm text-orange-600">
                {zones.filter(zone => zone.has_overdue_tasks).length} zona(s) con tareas de limpieza vencidas:
              </p>
              <ul className="mt-2 list-disc list-inside text-sm text-orange-600">
                {zones
                  .filter(zone => zone.has_overdue_tasks)
                  .map(zone => (
                    <li key={zone.id}>
                      <strong>{zone.name}</strong> ({zone.code}) - {zone.overdue_cleaning_tasks_count} tarea(s) vencida(s)
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Tipo de zona</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm"
            >
              <option value="all">Todos</option>
              <option value="production">Producción</option>
              <option value="storage">Almacén</option>
              <option value="preparation">Preparación</option>
              <option value="packaging">Empaquetado</option>
              <option value="other">Otro</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Estado</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm"
            >
              <option value="all">Todos</option>
              <option value="active">Activa</option>
              <option value="maintenance">Mantenimiento</option>
              <option value="inactive">Inactiva</option>
            </select>
          </div>
        </div>
      </div>

      {/* Zones Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
        </div>
      ) : filteredZones.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <MapPinIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay zonas</h3>
          <p className="mt-1 text-sm text-gray-500">Comienza agregando una nueva zona.</p>
          <div className="mt-6">
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Nueva Zona
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden rounded-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Zona
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Área
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tareas de Limpieza
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredZones.map((zone) => (
                <tr key={zone.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {zone.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {zone.code}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getTypeBadge(zone.type)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {zone.area_m2 ? `${zone.area_m2} m²` : 'No especificada'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {zone.pending_cleaning_tasks_count > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-50 text-yellow-800 border border-yellow-200">
                          {zone.pending_cleaning_tasks_count} pendiente(s)
                        </span>
                      )}
                      {zone.overdue_cleaning_tasks_count > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-800 border border-red-200">
                          <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                          {zone.overdue_cleaning_tasks_count} vencida(s)
                        </span>
                      )}
                      {zone.pending_cleaning_tasks_count === 0 && zone.overdue_cleaning_tasks_count === 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-800 border border-green-200">
                          <CheckCircleIcon className="w-3 h-3 mr-1" />
                          Al día
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(zone.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => viewZone(zone)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Ver detalles"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => editZone(zone)}
                        className="text-green-600 hover:text-green-900"
                        title="Editar"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => deleteZone(zone.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Eliminar"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-2xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingZone ? 'Editar Zona' : 'Nueva Zona'}
                  </h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nombre *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Código *
                      </label>
                      <input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Descripción
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Descripción de la zona..."
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Tipo *
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      >
                        <option value="production">Producción</option>
                        <option value="storage">Almacén</option>
                        <option value="preparation">Preparación</option>
                        <option value="packaging">Empaquetado</option>
                        <option value="other">Otro</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Área (m²)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.area_m2}
                        onChange={(e) => setFormData({ ...formData, area_m2: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Estado *
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      >
                        <option value="active">Activa</option>
                        <option value="maintenance">Mantenimiento</option>
                        <option value="inactive">Inactiva</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Notas
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Notas adicionales..."
                    />
                  </div>

                  {/* Element Frequencies Configuration */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Frecuencias de Limpieza por Elemento
                    </label>
                    {elementTypes.length === 0 || cleaningFrequencies.length === 0 ? (
                      <div className="text-sm text-gray-500">Cargando elementos y frecuencias...</div>
                    ) : (
                      <div className="space-y-3">
                        {elementTypes.map((elementType) => (
                        <div key={elementType.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{elementType.name}</p>
                            <p className="text-xs text-gray-500">Prioridad: {elementType.priority === 'critical' ? 'Crítica' : elementType.priority === 'high' ? 'Alta' : elementType.priority === 'medium' ? 'Media' : 'Baja'}</p>
                          </div>
                          <div>
                            <select
                              value={elementFrequencies[elementType.id] || cleaningFrequencies.find(f => f.code === 'daily')?.id || 1}
                              onChange={(e) => setElementFrequencies(prev => ({
                                ...prev,
                                [elementType.id]: parseInt(e.target.value)
                              }))}
                              className="text-sm border-gray-300 rounded-md shadow-sm"
                            >
                              {cleaningFrequencies.map((frequency) => (
                                <option key={frequency.id} value={frequency.id}>
                                  {frequency.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                          <p className="text-sm text-red-700">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {saving ? 'Guardando...' : (editingZone ? 'Actualizar' : 'Crear')}
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedZone && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-4xl w-full max-h-screen overflow-y-auto">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Detalles de la Zona: {selectedZone.name}
                  </h3>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Zone Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-md font-medium text-gray-900 mb-3">Información General</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Código</p>
                        <p className="mt-1 text-sm text-gray-900">{selectedZone.code}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Tipo</p>
                        <div className="mt-1">
                          {getTypeBadge(selectedZone.type)}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Área</p>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedZone.area_m2 ? `${selectedZone.area_m2} m²` : 'No especificada'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Estado</p>
                        <div className="mt-1">
                          {getStatusBadge(selectedZone.status)}
                        </div>
                      </div>
                    </div>
                    {selectedZone.description && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-500">Descripción</p>
                        <p className="mt-1 text-sm text-gray-900">{selectedZone.description}</p>
                      </div>
                    )}
                    {selectedZone.notes && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-500">Notas</p>
                        <p className="mt-1 text-sm text-gray-900">{selectedZone.notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Zone Elements */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-md font-medium text-gray-900">Elementos de Limpieza</h4>
                      <div className="flex space-x-2">
                        {editingFrequencies ? (
                          <>
                            <button
                              onClick={updateElementFrequencies}
                              disabled={saving || Object.keys(frequencyUpdates).length === 0}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                              {saving ? 'Guardando...' : 'Guardar Cambios'}
                            </button>
                            <button
                              onClick={() => {
                                setEditingFrequencies(false)
                                setFrequencyUpdates({})
                              }}
                              className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                            >
                              Cancelar
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setEditingFrequencies(true)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700"
                          >
                            <PencilIcon className="h-3 w-3 mr-1" />
                            Configurar Frecuencias
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                      <ul className="divide-y divide-gray-200">
                        {selectedZone.elements.map((element) => (
                          <li key={element.id} className="px-4 py-4">
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-3">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {element.name}
                                  </p>
                                  {getPriorityBadge(element.priority)}
                                  {element.is_cleaning_overdue && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-800 border border-red-200">
                                      <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                                      Vencido
                                    </span>
                                  )}
                                </div>
                                <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                                  {editingFrequencies ? (
                                    <div className="flex items-center space-x-2">
                                      <span>Frecuencia:</span>
                                      <select
                                        value={frequencyUpdates[element.id] || (() => {
                                          const currentFreq = cleaningFrequencies.find(f => f.hours === element.cleaning_frequency_hours);
                                          return currentFreq ? currentFreq.id : 1;
                                        })()}
                                        onChange={(e) => handleFrequencyChange(element.id, parseInt(e.target.value))}
                                        className="text-xs border-gray-300 rounded-md shadow-sm"
                                      >
                                        {cleaningFrequencies.map((frequency) => (
                                          <option key={frequency.id} value={frequency.id}>
                                            {frequency.name}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  ) : (
                                    <span>Frecuencia: {element.readable_frequency}</span>
                                  )}
                                  {element.pending_tasks_count > 0 && (
                                    <span>{element.pending_tasks_count} tarea(s) pendiente(s)</span>
                                  )}
                                </div>
                              </div>
                              <div className="text-sm text-gray-500">
                                {element.next_cleaning_due && (
                                  <p>Próxima limpieza: {fmtDate(element.next_cleaning_due)}</p>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Refrigeration Equipment */}
                  {selectedZone.refrigerators.length > 0 && (
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-3">Equipos de Refrigeración</h4>
                      <div className="bg-white shadow overflow-hidden sm:rounded-md">
                        <ul className="divide-y divide-gray-200">
                          {selectedZone.refrigerators.map((refrigerator) => (
                            <li key={refrigerator.id} className="px-4 py-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{refrigerator.name}</p>
                                  <p className="text-sm text-gray-500">{refrigerator.code}</p>
                                </div>
                                <div className="flex items-center space-x-3">
                                  <span className="text-sm text-gray-500">{refrigerator.type_name}</span>
                                  <span className="text-sm text-gray-500">{refrigerator.status_name}</span>
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}